import React, { useState, useEffect } from 'react'
import { useAppSelector } from '../../redux/hook'
import { LOGIN, selectAuth } from '../../redux/slices/authSlice'
import Modal from '../Modal'
import { store } from '../../redux/store'
import axios from '../../lib/axios'
import { auth } from '../../lib/apiList'
import { LoginResponse } from '../../features/auth/type'
import { useAppDispatch } from '../../redux/hook'
import { LOGOUT } from '../../redux/slices/authSlice'
import { User } from '../../features/auth/slice/userSlice'

interface Props {
    timeout: number
    onTimeout: () => void
}

const IdleTimer: React.FC<Props> = ({ timeout, onTimeout }) => {
    const state = useAppSelector(selectAuth)
    const dispatch = useAppDispatch()
    const [idle, setIdle] = useState<boolean>(false)
    const [showPopup, setShowPopup] = useState<boolean>(false)
    let timeoutId: NodeJS.Timeout

    const handleIdle = () => {
        setIdle(true)
        setShowPopup(true)
        onTimeout() // Trigger timeout callback when idle
    }

    const resetTimeout = () => {
        clearTimeout(timeoutId)
        setIdle(false)
        startIdleTimer() // Reset the idle timer after user activity
    }

    const startIdleTimer = () => {
        timeoutId = setTimeout(handleIdle, timeout)
    }

    useEffect(() => {
        if (state.isUser) {
            startIdleTimer() // Start the idle timer when the component mounts

            const handleUserActivity = () => {
                resetTimeout() // Reset the idle timer on user activity
            }

            // Set up event listeners for user activity
            !showPopup && window.addEventListener('click', handleUserActivity)
            !showPopup && window.addEventListener('keydown', handleUserActivity)
            !showPopup && window.addEventListener('scroll', handleUserActivity)

            // Add event listener for mousemove if popup is not shown
            if (!showPopup) {
                window.addEventListener('mousemove', handleUserActivity)
            }

            // Clean up event listeners when the component unmounts
            return () => {
                window.removeEventListener('click', handleUserActivity)
                window.removeEventListener('keydown', handleUserActivity)
                window.removeEventListener('scroll', handleUserActivity)
                window.removeEventListener('mousemove', handleUserActivity) // Remove mousemove event listener
                clearTimeout(timeoutId)
            }
        }
    }, [timeout, onTimeout, showPopup])

    // Reset the idle timer when the popup is closed or an action is performed
    useEffect(() => {
        if (!showPopup && idle) {
            resetTimeout()
        }
    }, [showPopup, idle])

    const generateRefreshToken = () => {
        try {
            if (state.token && state.refreshToken) {
                const { token, refreshToken } = state
                axios
                    .post(
                        auth.REFRESH_TOKEN,
                        { token, refreshToken },
                        {
                            headers: {
                                tenant: 'root',
                            },
                        }
                    )
                    .then((res: LoginResponse) => {
                        const newAuthTokens = res.data
                        axios.defaults.headers.common['Authorization'] =
                            'Bearer ' + newAuthTokens.token
                        store.dispatch(
                            LOGIN({
                                isUser: true,
                                token: newAuthTokens.token,
                                refreshToken: newAuthTokens.refreshToken,
                            })
                        )
                    })
            }
        } catch (error) {
            console.log(error)
        }
    }

    return (
        <div>
            {state.isUser && idle && (
                <Modal
                    show={showPopup}
                    handleClose={() => {
                        setShowPopup(false)
                        setIdle(false)
                        dispatch(
                            User({
                                role: '',
                                firstName: '',
                                email: '',
                                id: '',
                                lastName: '',
                            })
                        )
                        dispatch(LOGOUT())
                    }}
                    title="Alert"
                    hideCloseIcon={true}
                    body={
                        <>
                            <p>{` Your session is about to expire. You'll be automatically signed out.`}</p>
                            <p>{`Do you want to stay signed in?`}</p>
                        </>
                    }
                    defaultFooter={true}
                    onSuccess={() => {
                        setShowPopup(false)
                        setIdle(false)
                        generateRefreshToken()
                    }}
                />
            )}
        </div>
    )
}

export default IdleTimer
